'use client'

import classNames from 'classnames'
import {
  useTranslations,
  useSiteContext,
  useResponsive,
  useLayer,
} from '@dustin-web/microsite-components/src/hooks'
import {
  ModalDialog,
  ModalDialogHeader,
  ModalDialogContent,
} from '@dustin-web/microsite-components/src/modal-dialog'
import Popover from '@dustin-web/microsite-components/src/popover'
import Tag from '@dustin-web/microsite-components/src/tag'
import Tooltip from '@dustin-web/microsite-components/src/tooltip'
import ConditionalWrapper from '@dustin-web/microsite-components/src/utils/conditional-wrapper'
import { useState, useRef } from 'react'
import { useFavorites } from '../hooks/dataloader/use-favorites'
import { ProductDigitalData } from '../utils/product-digital-data'
import { Props } from './product-add-to-favorites'
import { FavoritesIconButton } from './favorites-icon-button'
import { GetFavoritesQuery } from '@dustin-web/microsite-data/src/graphql/product/generated/graphql-types'

export const FavoriteButton = ({
  digitalDataOrigin,
  quantity = 1,
  square = false,
  className = '',
  product,
  withBorder,
  onAddToFavoritesCompleted,
  noText = false,
  popoverLayer,
}: Props) => {
  const { translate } = useTranslations()
  const { isProxied } = useSiteContext()
  const [popoverOpen, setPopoverOpen] = useState(false)
  const [formData, setFormData] = useState('')
  const [formValid, setFormValid] = useState(true)
  const { updateFavorites, favoritesLists: favorites, notifyFavoriteClick } = useFavorites()
  const { breakpoints } = useResponsive()
  const buttonRef = useRef<HTMLButtonElement | null>(null)
  const layerContext = useLayer()

  const isPopover = breakpoints.min.MD
  const computedPopoverLayer = popoverLayer ?? layerContext ?? 'dialog'

  const addToFavoritesClass = `d-flex ${
    square ? 'c-add-to-favorites c-add-to-favorites--square' : 'c-add-to-favorites'
  } ${className}`

  const itemExist = favorites.some(item => {
    if (item) {
      return item?.items?.some(i => i?.productErpIdentifier === product.productErpIdentifier)
    } else {
      return false
    }
  })

  const submitForm = (e: React.SyntheticEvent) => {
    e.preventDefault()
    const valid = !!formData.length // TODO: Add some sort of validation

    // Validate input field
    if (!valid) {
      setFormValid(false)
      return false
    }

    addToList(formData)

    setFormValid(true)
  }

  const addToList = async (favoriteListName: string, favoriteListId?: number) => {
    ProductDigitalData.pushAddToWishList([product], digitalDataOrigin)
    await updateFavorites({
      productId: product.productErpIdentifier,
      displayName: product.displayName || '',
      favoriteListName,
      quantity,
      favoriteListId,
      favoriteList: { favorites: { productLists: favorites } } as GetFavoritesQuery,
    })

    notifyFavoriteClick('add', product.productErpIdentifier)

    if (onAddToFavoritesCompleted) {
      onAddToFavoritesCompleted()
    }
    resetState()
  }

  const resetState = () => {
    setFormData('')
    setPopoverOpen(false)

    if (isProxied) {
      const event = new CustomEvent('update-legacy-favorites-menu')
      document.dispatchEvent(event)
    }
  }

  return popoverOpen || withBorder ? (
    <>
      <FavoritesIconButton
        itemExist={itemExist}
        popoverOpen={popoverOpen}
        setPopoverOpen={x => setPopoverOpen(x)}
        withBorder={withBorder}
        noText={noText}
        buttonRef={buttonRef}
      />
      <ConditionalWrapper
        condition={isPopover}
        wrapper={children => (
          <Popover
            open={popoverOpen}
            onToggle={show => setPopoverOpen(show)}
            positions={['bottom-free']}
            targetRef={buttonRef}
            content={<>{children}</>}
            layer={computedPopoverLayer}
            closeOnOutsideClick
          />
        )}
        otherwise={children => (
          <ModalDialog show={popoverOpen} handleClose={() => setPopoverOpen(false)}>
            <ModalDialogHeader title={translate('Favorites_AddToFavoriteList')} />
            <ModalDialogContent>{children}</ModalDialogContent>
          </ModalDialog>
        )}>
        {favorites.length ? (
          <>
            <h4 className="u-mb-16">{translate('Favorites_AddToFavoriteList')}</h4>
            <div
              className="d-flex flex-wrap u-mb-16"
              style={{ maxWidth: '400px', overflowY: 'auto' }}>
              {favorites.map((list, i) => {
                const productListName = list?.productListName
                return (
                  productListName && (
                    <Tag
                      variant="choice"
                      key={i}
                      text={`${productListName} (${list!.items.length})`}
                      className={'t-light-blue'}
                      onClick={() => addToList(productListName, list!.id)}
                    />
                  )
                )
              })}
            </div>
          </>
        ) : (
          // TODO Maybe change copy?
          isPopover && <h4 className="u-mb-16">{translate('Favorites_AddToFavoriteList')}</h4>
        )}

        <form className="c-form c-form--dark" onSubmit={submitForm}>
          <div
            className={classNames('c-form__item row xs-gutters u-mb-0', {
              'c-form__error': !formValid,
            })}>
            <div className="col-8">
              <label className="u-sr-only" htmlFor="favouriteListName">
                {translate('Favorites_CreateNewFavoriteList')}
              </label>
              <input
                type="text"
                id="favouriteListName"
                className="u-mb-0"
                name="favouriteListName"
                value={formData}
                placeholder={translate('Favorites_CreateNewFavoriteList')}
                onChange={e => setFormData(e.target.value)}
              />
            </div>
            <div className="col-4" style={{ alignSelf: 'flex-end' }}>
              <button
                className="c-btn c-btn--primary c-btn--block u-mb-0 u-h-40"
                style={{ minWidth: '0' }}
                type="submit"
                value="Submit">
                {translate('Create')}
              </button>
            </div>
          </div>
        </form>
      </ConditionalWrapper>
    </>
  ) : (
    <Tooltip
      tooltipText={
        itemExist ? translate('Favorites_IsFavorite') : translate('Favorites_AddToFavorites')
      }
      className={addToFavoritesClass}>
      <FavoritesIconButton
        itemExist={itemExist}
        popoverOpen={popoverOpen}
        setPopoverOpen={x => setPopoverOpen(x)}
        withBorder={withBorder}
        noText={noText}
      />
    </Tooltip>
  )
}
